import { useEffect, useState } from "react";
import {
  ConvertedBooking,
  getBookingsList,
  WINTER_CLOSURE,
} from "../Contact/utils";
import { WEDDING_CALENDAR_ID } from "../Contact/PublicCalendar";

export type Availability = "loading" | "available" | "booked" | "unknown";

/**
 * Loads the public bookings calendar so the packages page can tell a visitor
 * whether the date they picked is still open. Shares the fetch and parsing
 * used by the availability calendar on the booking page.
 */
export const useBookedDates = () => {
  const [bookings, setBookings] = useState<ConvertedBooking[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadCalendar = () => {
      const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
      //@ts-ignore
      gapi.client
        .init({
          apiKey,
          discoveryDocs: [
            "https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest",
          ],
        })
        .then(() =>
          //@ts-ignore
          gapi.client.calendar.events.list({
            calendarId: WEDDING_CALENDAR_ID,
            timeZone: "America/Los_Angeles",
            singleEvents: true,
            timeMin: new Date().toISOString(),
            orderBy: "startTime",
          }),
        )
        .then((response: any) => {
          if (cancelled) return;
          setBookings(
            getBookingsList(response.result.items ?? [], [WINTER_CLOSURE]),
          );
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    };

    //@ts-ignore
    if (typeof gapi === "undefined") {
      setFailed(true);
      return;
    }
    //@ts-ignore
    gapi.load("client", loadCalendar);
    return () => {
      cancelled = true;
    };
  }, []);

  const startOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

  /**
   * A stay runs from check-in up to — but not including — check-out: the
   * calendar routinely hands one party's check-out day to the next party as
   * their check-in, so an end date is not itself blocked.
   */
  const isBooked = (date: Date) => {
    const day = startOfDay(date);
    return (bookings ?? []).some(
      (booking) =>
        day >= startOfDay(booking.start) && day < startOfDay(booking.end),
    );
  };

  const availabilityFor = (date: Date): Availability => {
    if (failed) return "unknown";
    if (!bookings) return "loading";
    return isBooked(date) ? "booked" : "available";
  };

  return { availabilityFor, isBooked, loaded: bookings !== null };
};
